import _classCallCheck from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/createClass.js";
import { logger } from '@ninetailed/experience.js-shared';
import { z } from 'zod';
var Audience = z.object({
  id: z.string(),
  name: z.string().optional(),
  description: z.string().optional()
});
var Config = z.object({
  distribution: z.array(z.number()).optional().default([0.5, 0.5]),
  traffic: z.number().optional().default(0),
  components: z.array(z.object({
    baseline: z.object({
      id: z.string().default('')
    }),
    variants: z.array(z.object({
      id: z.string().default(''),
      hidden: z.boolean().default(false)
    }))
  })).optional().default([{
    baseline: {
      id: ''
    },
    variants: [{
      id: '',
      hidden: false
    }]
  }]),
  sticky: z.boolean().optional().default(false)
});
var Variant = z.object({
  id: z.string()
}).catchall(z.unknown());

/**
 * Zod helper for parsing arrays and ignore items not specified in the schema
 *
 * @param zodUnion - union of known types
 *
 * @example
 * const binaryArraySchema = arrayIgnoreUnknown(z.union([z.literal('0'), z.literal('1')]))
 * type BinaryArray = z.TypeOf<typeof binaryArraySchema>
 *
 * const binaryArray: BinaryArray = binaryArraySchema.parse(['0', '1', '2', '0'])
 * console.log(binaryArray) // ['0', '1', '0']
 */
function zodArrayIgnoreUnknown(zodType) {
  var isKnownItem = function isKnownItem(item) {
    return zodType.safeParse(item).success;
  };
  return z.preprocess(function (val) {
    return toSafeArray(val).filter(isKnownItem);
  }, z.array(zodType));
}
function toSafeArray(item) {
  if (isArray(item)) {
    return item;
  }
  return [item];
}
function isArray(item) {
  return Array.isArray(item);
}
var ExperienceSchema = z.object({
  id: z.string(),
  /**
   * The name of the experience (Short Text)
   */
  name: z.string(),
  /**
   * The description of the experience (Short Text)
   */
  description: z.string().optional(),
  /**
   * The type if the experience (nt_experiment | nt_personalization)
   */
  type: z.union([z.string().regex(/^nt_experiment$/g), z.string().regex(/^nt_personalization$/g)]),
  /**
   * The config of the experience (JSON)
   */
  config: Config.default({}),
  /**
   * The audience of the experience (Audience)
   */
  audience: Audience.optional().nullable(),
  /**
   * All used variants of the experience (References to other Content Types)
   */
  variants: zodArrayIgnoreUnknown(Variant).default([])
});
var parse$1 = function parse$1(input) {
  var output = ExperienceSchema.parse(input);
  return Object.assign({}, output, {
    variants: output.variants
  });
};
var safeParse$1 = function safeParse$1(input) {
  var output = ExperienceSchema.safeParse(input);
  if (!output.success) {
    return output;
  }
  return Object.assign({}, output, {
    data: Object.assign({}, output.data, {
      variants: output.data.variants
    })
  });
};
var Experience = Object.assign({}, ExperienceSchema, {
  parse: parse$1,
  safeParse: safeParse$1
});
var ExperimentSchema = ExperienceSchema.extend({
  type: z.string().regex(/^nt_experiment$/g)
});
var parse = function parse(input) {
  var output = ExperimentSchema.parse(input);
  return Object.assign({}, output, {
    variants: output.variants
  });
};
var safeParse = function safeParse(input) {
  var output = ExperimentSchema.safeParse(input);
  if (!output.success) {
    return output;
  }
  return Object.assign({}, output, {
    data: Object.assign({}, output.data, {
      variants: output.data.variants
    })
  });
};
var Experiment = Object.assign({}, ExperimentSchema, {
  parse: parse,
  safeParse: safeParse
});
var ExperienceMapper = /*#__PURE__*/function () {
  function ExperienceMapper() {
    _classCallCheck(this, ExperienceMapper);
  }
  return _createClass(ExperienceMapper, null, [{
    key: "isExperienceEntry",
    value: function isExperienceEntry(experience) {
      return Experience.safeParse(experience).success;
    }
  }, {
    key: "mapExperience",
    value: function mapExperience(experience) {
      var parsedExperience = Experience.safeParse(experience);
      if (!parsedExperience.success) {
        logger.warn('[Ninetailed ExperienceMapper]', 'Error parsing experience', parsedExperience.error.format());
        throw new Error("[Ninetailed ExperienceMapper] The Experience Input is not valid. Please filter data first with \"ExperienceMapper.isExperienceEntry\".\n".concat(JSON.stringify(parsedExperience.error.format(), null, 2)));
      }
      var _parsedExperience$dat = parsedExperience.data,
        id = _parsedExperience$dat.id,
        type = _parsedExperience$dat.type,
        name = _parsedExperience$dat.name,
        description = _parsedExperience$dat.description,
        audience = _parsedExperience$dat.audience,
        config = _parsedExperience$dat.config,
        variants = _parsedExperience$dat.variants;
      var components = config.components,
        traffic = config.traffic,
        sticky = config.sticky;
      return Object.assign({
        id: id,
        type: type,
        name: name
      }, description ? {
        description: description
      } : {}, audience ? {
        audience: audience
      } : {}, {
        trafficAllocation: traffic,
        distribution: config.distribution.map(function (percentage, index) {
          return {
            index: index,
            start: config.distribution.slice(0, index).reduce(function (a, b) {
              return a + b;
            }, 0),
            end: config.distribution.slice(0, index + 1).reduce(function (a, b) {
              return a + b;
            }, 0)
          };
        }),
        sticky: sticky,
        components: components.map(function (component) {
          return {
            baseline: component.baseline,
            variants: component.variants.map(function (variantRef) {
              if (variantRef.hidden) {
                return variantRef;
              }
              var matchingVariant = variants.find(function (variant) {
                return variant.id === variantRef.id;
              });
              return matchingVariant != null ? matchingVariant : null;
            }).filter(function (variant) {
              return variant !== null;
            })
          };
        })
      });
    }
  }, {
    key: "isExperimentEntry",
    value: function isExperimentEntry(experiment) {
      return Experiment.safeParse(experiment).success;
    }
  }, {
    key: "mapExperiment",
    value: function mapExperiment(experiment) {
      return ExperienceMapper.mapExperience(experiment);
    }
  }]);
}();
export { Audience, Config, Experience, ExperienceMapper, Experiment, Variant };